import { custoConfig } from '@favo/core';
import { ArrowRight, Loader2, Plus } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Campo } from '@/componentes/campo';
import { CampoMoeda } from '@/componentes/campo-moeda';
import { Celula } from '@/componentes/celula';
import { RodapeFunil } from '@/componentes/rodape-funil';
import { Button } from '@/componentes/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card';
import { Input } from '@/componentes/ui/input';
import { Skeleton } from '@/componentes/ui/skeleton';
import { Valor } from '@/componentes/valor';
import type { ChaveCustoDireto, ConfigCusto, CustosDiretosProjeto, Integrador, Projeto as ProjetoDados } from '@/dados';
import { mensagemDoErro, useIntegrador, useProjetoAtual, useSalvarProjeto } from '@/dados';
import { formatarMoeda, formatarNumero, parseNum } from '@/design/formato';
import { Pagina } from '@/layout/pagina';

/* projeto.tsx — o projeto que está sendo orçado agora.
 *
 * É a tela que alimenta o dashboard. Três decisões carregam o resto:
 *
 * 1. "Custos do projeto" cobre TODOS os custos da empresa, cada um no seu
 *    caso: configurado como fixo vira campo pré-preenchido; sem configuração
 *    vira campo vazio (a etapa Custos promete o preenchimento manual aqui);
 *    derivado (por kWp, por módulo, por faixa) vira linha de leitura,
 *    recalculada ao vivo pelas medidas digitadas acima.
 *
 * 2. `custos_diretos` recebe SÓ o que o usuário viu como editável e deixou
 *    preenchido, sempre como NÚMERO. String é ignorada pelo motor e o
 *    override sumiria em silêncio; campo vazio não vira chave, porque
 *    "vazio" quer dizer "vale o padrão da empresa", não "vale zero".
 *
 * 3. Nenhum derivado é gravado. O motor recalcula da configuração a cada
 *    leitura; gravar o número congelaria o custo de hoje num projeto que
 *    amanhã seria recalculado diferente.
 */

const ORDEM_CUSTO: readonly ChaveCustoDireto[] = ['ca', 'inst', 'proj', 'homol', 'art'];

const ROTULO_CUSTO: Record<ChaveCustoDireto, string> = {
  ca: 'Material adicional CA',
  inst: 'Instalação',
  proj: 'Projeto elétrico',
  homol: 'Homologação',
  art: 'ART do projeto',
};

/** Um custo da empresa como esta tela precisa dele. */
type ItemCusto = {
  /** Chave do estado local do formulário. */
  chave: string;
  rotulo: string;
  cfg: ConfigCusto | undefined;
  /** Derivado = calculado pelas medidas do sistema. Não é editável aqui. */
  derivado: boolean;
  /** Uma das cinco linhas fixas, ou null quando é extra da empresa. */
  campo: ChaveCustoDireto | null;
  /** Id do extra, para o override em `custos_diretos.extras[id]`. */
  extraId: string | null;
};

/** Ordem em que a validação procura o primeiro campo inválido. */
const ORDEM_FOCO = ['nome', 'modulos', 'kwp', 'kit'] as const;
type CampoFoco = (typeof ORDEM_FOCO)[number];

export function Projeto() {
  const [busca] = useSearchParams();
  // ?novo=1: começa um projeto NOVO. Sem o parâmetro, a tela edita o projeto
  // mexido por último.
  const modoNovo = busca.has('novo');

  const integrador = useIntegrador();
  const projetoAtual = useProjetoAtual();

  const carregando = integrador.isPending || projetoAtual.isPending;
  const ultimo = projetoAtual.data ?? null;
  const emEdicao = modoNovo ? null : ultimo;
  const referencia = modoNovo ? ultimo : null;

  return (
    <Pagina
      titulo={modoNovo ? 'Novo projeto' : 'Dados do projeto'}
      descricao="Medidas do sistema, custos deste projeto e o preço praticado hoje."
      etapa="projeto"
      acoes={
        !carregando && emEdicao ? (
          <Button variant="outline" asChild>
            <Link to="/projeto?novo=1">
              <Plus /> Novo projeto
            </Link>
          </Button>
        ) : null
      }
    >
      {carregando ? (
        <Carregando />
      ) : integrador.isError ? (
        <Falha
          mensagem={mensagemDoErro(integrador.error)}
          aoTentar={() => void integrador.refetch()}
        />
      ) : !integrador.data ? (
        <SemEmpresa />
      ) : (
        <Formulario
          // Trocar entre editar e criar remonta o formulário: o estado inicial
          // é lido uma vez, na montagem, e sem a chave ele sobreviveria à
          // troca de modo com os dados do projeto anterior dentro.
          key={emEdicao?.id ?? 'novo'}
          integrador={integrador.data}
          projeto={emEdicao}
          referencia={referencia}
        />
      )}
    </Pagina>
  );
}

function Formulario({
  integrador,
  projeto,
  referencia,
}: {
  integrador: Integrador;
  /** Projeto em edição. null quando é projeto novo (vira insert ao salvar). */
  projeto: ProjetoDados | null;
  /** Último projeto, só como placeholder das medidas em projeto novo. */
  referencia: ProjetoDados | null;
}) {
  const navegar = useNavigate();
  const salvar = useSalvarProjeto();

  const configs = integrador.configs;

  const itens = React.useMemo<ItemCusto[]>(() => {
    const campos = configs?.campos ?? {};

    const fixos: ItemCusto[] = ORDEM_CUSTO.map((chave) => ({
      chave,
      rotulo: ROTULO_CUSTO[chave],
      cfg: campos[chave],
      derivado: ehDerivado(campos[chave]),
      campo: chave,
      extraId: null,
    }));

    // Extra sem rótulo é linha que o usuário abriu no formulário de custos e
    // não preencheu: o motor a ignora, e a tela também.
    const adicionais: ItemCusto[] = (configs?.extras ?? [])
      .filter((extra) => extra?.label)
      .map((extra, i) => ({
        chave: `extra:${extra.id ?? i}`,
        rotulo: extra.label ?? '',
        cfg: extra,
        derivado: ehDerivado(extra),
        campo: null,
        // `?? ''` acompanha o motor, que também indexa o override por ''
        // quando o extra foi salvo sem id.
        extraId: extra.id ?? '',
      }));

    return [...fixos, ...adicionais];
  }, [configs]);

  const [nome, setNome] = React.useState(() => projeto?.nome_projeto ?? '');
  const [modulos, setModulos] = React.useState(() => textoDeMedida(projeto?.quantidade_modulos));
  const [kwp, setKwp] = React.useState(() => textoDeMedida(projeto?.kwp_sistema));
  const [kit, setKit] = React.useState<number | null>(() => positivoDoBanco(projeto?.custo_kit));
  const [venda, setVenda] = React.useState<number | null>(() => positivoDoBanco(projeto?.valor_venda));
  const [custos, setCustos] = React.useState<Record<string, number | null>>(() =>
    custosIniciais(itens, projeto),
  );
  const [erros, setErros] = React.useState<Partial<Record<CampoFoco, string>>>({});
  const [erroSalvar, setErroSalvar] = React.useState<string | null>(null);

  const refs: Record<CampoFoco, React.RefObject<HTMLInputElement | null>> = {
    nome: React.useRef<HTMLInputElement>(null),
    modulos: React.useRef<HTMLInputElement>(null),
    kwp: React.useRef<HTMLInputElement>(null),
    kit: React.useRef<HTMLInputElement>(null),
  };

  // Medidas ao vivo: os derivados abaixo saem daqui a cada tecla, sem estado
  // intermediário para dessincronizar.
  const mods = medida(modulos);
  const kwpAtual = medida(kwp);

  function validar(): Partial<Record<CampoFoco, string>> {
    const achados: Partial<Record<CampoFoco, string>> = {};
    if (nome.trim().length < 2) achados.nome = 'Informe o nome do projeto.';
    // type=number entrega o valor com ponto decimal: parseFloat, nunca o
    // parser pt-BR. E módulo é contagem: 7,5 módulos não existe.
    const qtd = Number.parseFloat(modulos);
    if (!Number.isInteger(qtd) || qtd <= 0) {
      achados.modulos = 'Informe a quantidade de módulos em número inteiro.';
    }
    if (!(Number.parseFloat(kwp) > 0)) achados.kwp = 'Informe a potência do sistema.';
    if (kit === null || !(kit > 0)) achados.kit = 'Informe o custo do KIT.';
    return achados;
  }

  function focarPrimeiroInvalido(achados: Partial<Record<CampoFoco, string>>) {
    const alvo = ORDEM_FOCO.find((chave) => achados[chave]);
    if (!alvo) return;
    const elemento = refs[alvo].current;
    // preventScroll + scrollIntoView: o pulo seco do foco desorienta, e sem
    // rolagem nenhuma o botão parece morto quando o erro está fora da tela.
    elemento?.focus({ preventScroll: true });
    // `?.()` na rolagem: jsdom não implementa scrollIntoView, e sem o guarda
    // qualquer teste que submeta o formulário inválido estoura aqui.
    elemento?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }

  /** Só o que é editável e foi preenchido, sempre como número. */
  function coletarCustosDiretos(): CustosDiretosProjeto {
    const diretos: CustosDiretosProjeto = {};
    const extras: Record<string, number> = {};

    for (const item of itens) {
      if (item.derivado) continue;
      const valor = custos[item.chave];
      // Vazio não vira chave: significa "vale o padrão da empresa". Zero
      // digitado é override legítimo, por isso o teste é contra null.
      if (valor === null || valor === undefined) continue;
      if (item.campo) diretos[item.campo] = valor;
      else if (item.extraId !== null) extras[item.extraId] = valor;
    }

    if (Object.keys(extras).length > 0) diretos.extras = extras;
    return diretos;
  }

  async function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault();

    const achados = validar();
    setErros(achados);
    if (Object.keys(achados).length > 0) {
      focarPrimeiroInvalido(achados);
      return;
    }

    setErroSalvar(null);
    try {
      await salvar.mutateAsync({
        // Sem id o hook faz insert; com id, atualiza o projeto aberto.
        ...(projeto?.id ? { id: projeto.id } : {}),
        nome_projeto: nome.trim(),
        quantidade_modulos: Number.parseFloat(modulos),
        kwp_sistema: Number.parseFloat(kwp),
        custo_kit: kit,
        valor_venda: venda,
        custos_diretos: coletarCustosDiretos(),
      });
      navegar('/');
    } catch (erro) {
      // Erro de gravação mantém tudo em tela: o que foi digitado continua
      // aqui, e a mensagem diz o que aconteceu.
      setErroSalvar(mensagemDoErro(erro));
    }
  }

  return (
    <form onSubmit={enviar} noValidate className="favo">
      <Card className="gap-4">
        <CardHeader>
          <CardTitle>Sistema</CardTitle>
          <CardDescription>
            Medidas do sistema fotovoltaico. São elas que resolvem os custos por kWp, por
            módulo e por faixa.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 @campos:grid-cols-2">
          <Campo
            rotulo="Nome do projeto"
            obrigatorio
            erro={erros.nome}
            className="@campos:col-span-2"
          >
            {(atributos) => (
              <Input
                {...atributos}
                ref={refs.nome}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex.: Residencial João Silva"
                autoComplete="off"
              />
            )}
          </Campo>

          <Campo
            rotulo="Quantidade de módulos"
            obrigatorio
            erro={erros.modulos}
            ajuda="Número de placas instaladas."
          >
            {(atributos) => (
              <Input
                {...atributos}
                ref={refs.modulos}
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                className="numero text-right"
                value={modulos}
                onChange={(e) => setModulos(e.target.value)}
                placeholder={placeholderMedida(referencia?.quantidade_modulos, '0')}
              />
            )}
          </Campo>

          <Campo
            rotulo="Potência do sistema"
            obrigatorio
            erro={erros.kwp}
            ajuda="Potência total instalada, em kWp."
          >
            {(atributos) => (
              <Input
                {...atributos}
                ref={refs.kwp}
                type="number"
                min={0.01}
                step={0.01}
                inputMode="decimal"
                className="numero text-right"
                value={kwp}
                onChange={(e) => setKwp(e.target.value)}
                placeholder={placeholderMedida(referencia?.kwp_sistema, '0,00')}
              />
            )}
          </Campo>
        </CardContent>
      </Card>

      <Card className="gap-4">
        <CardHeader>
          <CardTitle>Custos do projeto</CardTitle>
          <CardDescription>
            Os valores vêm do padrão da etapa Custos. Ajuste se este projeto for diferente.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid items-start gap-4 @campos:grid-cols-2">
          <CampoMoeda
            className="@campos:col-span-2"
            rotulo="Custo do KIT"
            obrigatorio
            valor={kit}
            aoMudar={setKit}
            erro={erros.kit}
            ajuda="Valor faturado pela distribuidora para este projeto."
            placeholder="0,00"
            ref={refs.kit}
          />

          {itens.map((item) =>
            item.derivado ? (
              <CustoDerivado
                key={item.chave}
                rotulo={item.rotulo}
                cfg={item.cfg}
                kwp={kwpAtual}
                mods={mods}
              />
            ) : (
              <CampoMoeda
                key={item.chave}
                rotulo={item.rotulo}
                valor={custos[item.chave] ?? null}
                aoMudar={(valor) =>
                  setCustos((atual) => ({ ...atual, [item.chave]: valor }))
                }
                ajuda={ajudaDoEditavel(item.cfg)}
                placeholder="0,00"
              />
            ),
          )}
        </CardContent>
      </Card>

      <Card className="gap-4">
        <CardHeader>
          <CardTitle>Preço praticado</CardTitle>
          <CardDescription>
            Quanto este projeto está sendo vendido hoje, kit mais serviço. É o número que a
            próxima etapa audita.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CampoMoeda
            rotulo="Preço de venda atual"
            valor={venda}
            aoMudar={setVenda}
            ajuda="Deixe vazio se este projeto ainda não tem preço."
            placeholder="0,00"
          />
        </CardContent>
      </Card>

      {erroSalvar ? (
        <Celula campo="telha" respiro="justo" role="alert" className="text-negativo text-apoio">
          Não foi possível salvar o projeto. {erroSalvar}
        </Celula>
      ) : null}

      <RodapeFunil etapa="projeto">
        <Button type="submit" disabled={salvar.isPending} aria-busy={salvar.isPending}>
          {salvar.isPending ? (
            <>
              <Loader2 className="animate-spin" /> Salvando…
            </>
          ) : (
            <>
              {projeto ? 'Atualizar projeto' : 'Analisar precificação'} <ArrowRight />
            </>
          )}
        </Button>
      </RodapeFunil>
    </form>
  );
}

/* ── Custo derivado: leitura, não campo ──────────────────────────────────── */

function CustoDerivado({
  rotulo,
  cfg,
  kwp,
  mods,
}: {
  rotulo: string;
  cfg: ConfigCusto | undefined;
  kwp: number;
  mods: number;
}) {
  const valor = custoConfig(cfg, kwp, mods);

  return (
    // `gap-2` e `.rotulo` são os mesmos do <Campo>: a linha derivada senta na
    // grade ao lado de um campo editável, e um degrau de rótulo diferente
    // fazia as duas colunas parecerem de blocos distintos.
    <div className="grid content-start gap-2">
      <span className="rotulo">{rotulo}</span>
      {/* Caixa preenchida, sem borda nem foco: o visual diz "isto o sistema
          calcula por você". Preenche a célula, igual ao campo vizinho. */}
      <div className="bg-areia flex h-toque items-center justify-end rounded-lg px-3">
        {valor === null ? (
          <span className="text-muted-foreground text-apoio">aguardando dados do sistema</span>
        ) : (
          <Valor valor={valor} formato="moeda" />
        )}
      </div>
      <p className="apoio-nota">
        {valor === null ? regraDoCusto(cfg) : detalheDoCalculo(cfg, kwp, mods)}
      </p>
    </div>
  );
}

/* ── Textos de apoio ─────────────────────────────────────────────────────── */

function ajudaDoEditavel(cfg: ConfigCusto | undefined): string {
  if (cfg?.modo === 'fixo') {
    return `Padrão da empresa: ${formatarMoeda(parseNum(cfg.taxa))}. O valor digitado vale só neste projeto.`;
  }
  return 'Sem padrão da empresa, informe neste projeto.';
}

/** A regra configurada, quando ainda não há medidas para aplicá-la. */
function regraDoCusto(cfg: ConfigCusto | undefined): string {
  if (cfg?.modo === 'kwp') return `${formatarMoeda(parseNum(cfg.taxa))} por kWp`;
  if (cfg?.modo === 'modulo') return `${formatarMoeda(parseNum(cfg.taxa))} por módulo`;
  if (cfg?.modo === 'faixa') {
    return `por faixa de ${cfg.faixaVariavel === 'modulos' ? 'módulos' : 'kWp'}`;
  }
  return 'valor fixo';
}

/** A conta que produziu o valor exibido, com os números desta tela. */
function detalheDoCalculo(cfg: ConfigCusto | undefined, kwp: number, mods: number): string {
  if (cfg?.modo === 'kwp') {
    return `${formatarMoeda(parseNum(cfg.taxa))}/kWp × ${formatarNumero(kwp, { casasMax: 2 })} kWp`;
  }
  if (cfg?.modo === 'modulo') {
    return `${formatarMoeda(parseNum(cfg.taxa))}/módulo × ${formatarNumero(mods, { casasMax: 0 })} módulos`;
  }
  if (cfg?.modo === 'faixa') {
    const base =
      cfg.faixaVariavel === 'modulos'
        ? `${formatarNumero(mods, { casasMax: 0 })} módulos`
        : `${formatarNumero(kwp, { casasMax: 2 })} kWp`;
    return `faixa da empresa aplicada a ${base}`;
  }
  return '';
}

/* ── Leitura dos dados que chegam do banco ───────────────────────────────── */

function ehDerivado(cfg: ConfigCusto | undefined): boolean {
  return Boolean(cfg?.modo) && cfg?.modo !== 'fixo';
}

/**
 * Número gravado no banco. Coluna `numeric` já chega convertida na borda da
 * API, e override de jsonb só vale como number (string o motor ignora): nos
 * dois casos, o teste é o tipo, e não um parser de texto.
 */
function numeroDoBanco(valor: unknown): number | null {
  return typeof valor === 'number' && Number.isFinite(valor) ? valor : null;
}

/** Como `numeroDoBanco`, mas zero conta como "não informei". */
function positivoDoBanco(valor: unknown): number | null {
  const numero = numeroDoBanco(valor);
  return numero !== null && numero > 0 ? numero : null;
}

/** Valor de um `<input type="number">`: ponto decimal, formato da máquina. */
function textoDeMedida(valor: unknown): string {
  const numero = numeroDoBanco(valor);
  return numero === null || numero <= 0 ? '' : String(numero);
}

/**
 * Em projeto novo, o último projeto vira sugestão visível: o usuário vê o de
 * sempre sem redigitar, mas nada entra sozinho no campo.
 */
function placeholderMedida(valor: unknown, vazio: string): string {
  const numero = numeroDoBanco(valor);
  return numero === null || numero <= 0 ? vazio : String(numero);
}

/** Medida digitada agora. Zero quando ainda não dá para calcular com ela. */
function medida(texto: string): number {
  const numero = Number.parseFloat(texto);
  return Number.isFinite(numero) && numero > 0 ? numero : 0;
}

function custosIniciais(
  itens: ItemCusto[],
  projeto: ProjetoDados | null,
): Record<string, number | null> {
  const salvos = projeto?.custos_diretos ?? {};
  const extrasSalvos = salvos.extras ?? {};
  const estado: Record<string, number | null> = {};

  for (const item of itens) {
    if (item.derivado) continue;
    // Precedência: ajuste salvo no projeto, padrão fixo da empresa, vazio.
    const salvo = item.campo
      ? numeroDoBanco(salvos[item.campo])
      : numeroDoBanco(extrasSalvos[item.extraId ?? '']);
    if (salvo !== null) {
      estado[item.chave] = salvo;
      continue;
    }
    estado[item.chave] = item.cfg?.modo === 'fixo' ? parseNum(item.cfg.taxa) : null;
  }

  return estado;
}

/* ── Estados da tela ─────────────────────────────────────────────────────── */

function Carregando() {
  return (
    <div className="favo">
      {[0, 1].map((i) => (
        <Card key={i} className="gap-4">
          <CardHeader>
            <Skeleton className="h-4 w-40" />
          </CardHeader>
          <CardContent className="grid gap-4 @campos:grid-cols-2">
            <Skeleton className="h-9 w-full @campos:col-span-2" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function SemEmpresa() {
  return (
    <Card>
      <CardContent className="grid justify-items-start gap-3">
        <p className="titulo-bloco">Cadastre a empresa antes de criar projetos</p>
        <p className="apoio">
          Os custos e as despesas da empresa são o que transforma as medidas deste projeto em
          preço.
        </p>
        <Button asChild>
          <Link to="/empresa">Ir para Empresa</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

/* Campo telha, o mesmo de toda falha no produto. O SemEmpresa acima continua
   em nata de propósito: falta de cadastro é pendência, não perda. */
function Falha({ mensagem, aoTentar }: { mensagem: string; aoTentar: () => void }) {
  return (
    <Celula campo="telha" respiro="folgado" role="alert" className="grid justify-items-start gap-3">
      <p className="titulo-bloco">Não foi possível carregar os dados da empresa</p>
      <p className="apoio">{mensagem}</p>
      <Button variant="outline" onClick={aoTentar}>
        Tentar de novo
      </Button>
    </Celula>
  );
}
